import React from "react";
import "./TaxNumbersFlow.css";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);

const FlowRow = ({
  code,
  label,
  value,
  variant,
  onClick,
  clickable,
  className,
  editable = false,
  isEditing = false,
  onValueChange,
}) => {
  const handleClick = () => {
    if (onClick) onClick();
  };

  // 🔸 Special styling for the "button" (code pill)
  const codeStyle =
    variant === "income"
      ? {
          backgroundColor: " #E8F1F8", // orange pill
          color: "#000000ff",
        }
      : undefined;

  return (
    <div
      className={`tax-flow-row 
        ${variant === "final" ? "tax-flow-row--final" : ""}
        ${variant === "taxable" ? "tax-flow-row--taxable" : ""}
        ${variant === "tax" ? "tax-flow-row--tax" : ""}
        ${variant === "income" ? "tax-flow-row--income" : ""}
        ${variant === "deduction" ? "tax-flow-row--deductions" : ""}
        ${variant === "nonref" ? "tax-flow-row--nonref" : ""}
        ${variant === "ref" ? "tax-flow-row--ref" : ""}
        ${variant === "stded" ? "tax-flow-row--stded" : ""}
        ${variant === "AGI" ? "tax-flow-row--AGI" : ""}
        ${clickable ? "tax-flow-row--clickable" : ""}
        ${className || ""}
      `}
      onClick={handleClick}
    >
      <div className="tax-flow-row-left">
        <div className="tax-flow-code" style={codeStyle}>
          {code}
        </div>
        <div className="tax-flow-label">{label}</div>
      </div>

      <div className="tax-flow-value">
        {editable && isEditing ? (
          <input
            type="number"
            className="tax-flow-edit-input"
            value={value}
            onChange={(e) =>
              onValueChange && onValueChange(e.target.value)
            }
            onClick={(e) => e.stopPropagation()} // don't re-trigger row click
          />
        ) : (
          formatCurrency(value)
        )}
      </div>
    </div>
  );
};

const TaxNumbersFlow = ({
  income,
  deductions,
  agi,
  standardDeduction,
  taxableIncome,
  taxBeforeCredits,
  nonrefundableCredits,
  refundableCredits,
  finalNumber,
  finalLabel = "1",
  onAGIClick,
  onDeductionClick,
  isEditingDeductions = false,
  onDeductionsChange,
}) => {
  return (
    <div className="tax-flow">
      <div className="tax-flow-tagline">
        <span className="tax-flow-tagline-pill">Sample illustration</span>
        <span className="tax-flow-tagline-text"></span>
      </div>

      <div className="tax-flow-body">
        {/* Income */}
        <FlowRow
          code="Income for the year"
          value={income}
          variant="income"
        />

        {/* Above-the-line deductions — now editable when clicked */}
        <FlowRow
          code="-"
          label="Above the Line Deductions"
          value={deductions}
          clickable
          variant="deduction"
          onClick={onDeductionClick}
          editable
          isEditing={isEditingDeductions}
          onValueChange={(val) =>
            onDeductionsChange &&
            onDeductionsChange(Number(val) || 0)
          }
        />

        {/* AGI */}
        <FlowRow
          code="="
          label="Adjusted Gross Income"
          value={agi}
          variant="AGI"
          onClick={onAGIClick}
        />

        {/* Below-the-line / standard deduction */}
        <FlowRow
          code="-"
          label="Below the Line Deductions"
          value={standardDeduction}
          variant="stded"
        />

        {/* Taxable income */}
        <FlowRow
          code="="
          label="Taxable Income"
          value={taxableIncome}
          variant="taxable"
        />

        {/* Tax before credits */}
        <FlowRow
          code="*"
          label="Tax Rate"
          value={taxBeforeCredits}
          variant="tax"
        />

        {/* Nonrefundable credits */}
        <FlowRow
          code="-"
          label="Non-refundable Credits"
          value={nonrefundableCredits}
          variant="nonref"
        />

        {/* Refundable credits */}
        <FlowRow
          code="-"
          label="Refundable Credits"
          value={refundableCredits}
          variant="ref"
        />

        <div className="tax-flow-divider" />

        {/* Final tax or refund */}
        <FlowRow
          code="="
          label={finalLabel}
          value={finalNumber}
          variant="final"
          className={
            finalNumber >= 0
              ? "tax-flow-final--due"
              : "tax-flow-final--refund"
          }
        />
      </div>
    </div>
  );
};

export default TaxNumbersFlow;
